/**
 * 작성자 : 홍선영
 * 날짜 : 2023.08.11
 * 기능 : 대시보드 컴포넌트
 */

import { startTransition, useCallback, useEffect, useRef, useState } from 'react';
import { v1 } from 'uuid';
import { useQuery } from '@tanstack/react-query';
import { PulseLoader } from 'react-spinners';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { DndProvider, useDrag } from 'react-dnd';
import { useRecoilState, useRecoilValue } from 'recoil';

import dayjs from 'dayjs';
import styled from 'styled-components';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import 'dayjs/locale/ko'; // Import Korean locale

import { asideFoldState, themeState, userState } from '../atoms';
import { DASHBOARD_GUBUN, MOBILE_NOT_SUPPORT } from '../_constants';
import { IComCd, IComCdList, IDashboard, IDashboardM } from 'customTypes';
import { BtnDeep } from './Button';
import Portal from './Portal';
import MultiVideoPlayer from './MultipleVideoPlayer3';
import { isMobile } from '../utils/checkMobile';
import { arraySortByAscdOrder } from '../utils/arraySortByAscdOrder';
import notSupport from '../assets/images/illustration/78.svg';
import monitoring from '../assets/images/illustration/74.svg';
import WebSdkModal from './Modal/WebSdkModal';
import * as cctv from '../utils/cctv';
import WebSdk from '../pages/s_cctv/WebSdk';
import MGasInfo from './dashboard/MGasInfo';
import SensorStatusWidget from './dashboard/SensorStatus';
import ExcavationProcess from './dashboard/ExcavationProcess';
import ExcavationProcessLarge from './dashboard/ExcavationProcessLarge';
import TunnelTotalInfo from './dashboard/TunnelTotalInfo';
import TunnelAreaInfo from './dashboard/TunnelAreaInfo';
import TunnelTotalTable from './dashboard/TunnelTotalTable';
import { WeatherInfo, WeatherWeekInfo } from './dashboard/Weather';
import { TotalInfo } from './dashboard/TotalInfo';
import { NewOldWorker, NewWorker, OldWorker, OldWorkerAttend, VisitorList, WorkerAttend } from './dashboard/WorkerList';
import { AttendTotalInfo } from './dashboard/AttendTotalInfo';
import { SafetyViolationInfo } from './dashboard/SafetyViolationInfo';
import { AttendInfo } from './dashboard/AttendInfo';
import { Dday } from './dashboard/Dday';
import { EquipInfo } from './dashboard/EquipInfo';
import { PmInfo } from './dashboard/PmInfo';
import { AccidentInfo } from './dashboard/AccidentInfo';
import { SafetyViolationInfoDetail } from './dashboard/SafetyViolationInfoDetail';
import { ExcavationWorkerPosition } from './dashboard/ExcavationWorkerPosition';
import SelectBox from './SelectBox';
import { apiGet } from '../services/_common';
import WeatherTotal from './dashboard/WeatherTotal';
import WGasInfo from './dashboard/WGasInfo';
import GetFetchStatus from '../hooks/GetFetchStatus';
import BigCalendar from './BigCalendar';
import { useCalendar } from '../hooks/useCalendar';
import { SiteView } from './dashboard/SiteView';
import { SiteInfo } from './dashboard/SiteInfo';
import { AnalogClock, ClockCalendar, DigitalClock } from './dashboard/Clock';
import WeekCalendar from './dashboard/WeekCalendar';
import SensorMonitorItem from './dashboard/SensorMonitor/SensorMonitorItem';

dayjs.extend(weekday);
dayjs.extend(weekOfYear);
dayjs.locale('ko'); // Set the locale to Korean

interface IProps {
  isViewMode: boolean; // 세팅화면인지/뷰화면인지 여부
}

interface RootStyleProps extends IProps {
  maxRowCount: number;
  loading: boolean;
}

interface DraggableCellStyleProps {
  isDragging: boolean;
}

interface DroppableCellStyleProps extends IProps {
  cellHeight: number;
  cellWidth: number;
}

const Root = styled.div<RootStyleProps>`
  filter: ${(props) => (props.loading ? 'blur(0.1rem)' : '')};

  .tui-container {
    flex-grow: 1;
    overflow: hidden;
    margin-left: 0.5rem;
  }

  .dragNDropZone {
    display: flex;
    justify-content: space-between;
    align-items: start;

    .dragZone {
      display: ${(props) => (props.isViewMode ? `none !important` : `flex`)};
      flex: 0.15;
      display: flex;
      flex-direction: column;
      .dragCell {
        margin: 0.5rem 0;
      }
    }

    .dropZone {
      flex: ${(props) => (props.isViewMode ? `1` : `0.85`)};
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      grid-template-rows: repeat(${(props) => props.maxRowCount}, 1fr);
      gap: 1rem;

      > div {
        overflow: hidden;
        margin: 0;
        background-color: var(--board);
      }
    }
  }

  @media screen and (max-width: 1679px) {
    .dragNDropZone .dropZone {
      display: flex;
      flex-direction: row;
      flex-flow: wrap;
      width: calc(100% - 16rem);
      justify-content: space-between;
    }
  }

  @media screen and (max-width: 1024px) {
    .dragNDropZone .dropZone {
      display: flex;
      flex-wrap: wrap;
    }
  }
`;

const DraggableCellStyle = styled.div<DraggableCellStyleProps>`
  // min-width: min-content;
  background-color: #5ac8fa;
  color: #fff;
  padding: 0.5rem;
  border-radius: 5px;
  text-align: center;
  margin-bottom: 1rem;
  cursor: pointer;
  opacity: ${(props) => (props.isDragging ? '0.5' : '1')};
  height: 90%;
`;

const DroppableCellStyle = styled.div<DroppableCellStyleProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: fit-content;
  border-radius: 5px;
  box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.03);
  /* outline: var(--outline-status); */
  outline: var(--outline-status);

  .dropCell {
    width: 100%;
    height: ${(props) => props.cellHeight}rem;

    @media screen and (max-width: 480px) {
      // 현재 날씨 위젯 모바일 높이 조절
      &[data-cell-id='103'] {
        height: auto;
      }
    }
  }

  .closeIconWrapper {
    display: ${(props) => (props.isViewMode ? `none !important` : `flex`)};
  }

  .closeIcon {
    font-size: 24px;
    color: yellow;
    transition: transform 0.1s ease-in-out;

    &:hover {
      transform: scale(1.2);
    }
  }

  @media screen and (max-width: 1679px) {
    width: ${(props) => (props.cellWidth === 1 ? 'calc(50% - 0.5rem)' : props.cellWidth === 2 ? '100%' : props.cellWidth === 3 ? '100%' : '100%')};
    /* width: ${(props) => (props.cellWidth === 1 ? 'calc(50% - 0.5rem)' : props.cellWidth === 2 ? '100%' : props.cellWidth === 3 ? '66.6%' : '100%')}; */
  }

  @media screen and (max-width: 767px) {
    width: 100%;
  }
`;

interface DraggableCellProps {
  cell: any;
}

interface Piece {
  dCd: string;
  name: string;
  width: number;
  height: number;
  isInDropZone: boolean;
  location: { column: number; row: number; mRow?: number };
}

const DraggableCell = ({ cell }: DraggableCellProps) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'cell',
    item: cell,
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  return <DraggableCellStyle ref={drag} isDragging={isDragging} />;
};

const DroppableZone = ({ children, cell, isViewMode }: any) => {
  return (
    <DroppableCellStyle
      style={{
        gridColumnStart: cell.location.column + 1,
        gridRowStart: cell.location.row + 1,
        gridColumnEnd: cell.location.column + cell.width + 1,
        gridRowEnd: cell.location.row + cell.height + 1,
        backgroundColor: cell.name === '' ? '#fff' : undefined,
      }}
      isViewMode={isViewMode}
      cellHeight={cell.height * 13 - 1}
      cellWidth={cell.width}
    >
      <div className='dropCell' style={cell.name === '' ? { display: 'none' } : { display: 'block' }} data-cell-id={cell.dCd}>
        <Control content={children} />
      </div>
    </DroppableCellStyle>
  );
};

const DashboardSetting = ({ isViewMode }: IProps) => {
  const userInfo = useRecoilValue(userState);
  const [reLocatedDragPieces, setReLocatedDragPieces] = useState<Piece[]>([]); // 위치 수정한 드래그피스 데이터(width 조절시 다시 되돌아가기 위한 state값)
  const [dropZoneGrid, setDropZoneGrid] = useState<any[]>([]);
  const [dashboardMList, setDashboardMList] = useState<Piece[]>([]);
  const [dragPieces, setDragPieces] = useState<Piece[]>([]);
  const dGubun = userInfo.sCd === '00000' ? (userInfo.hCd?.substring(0, 1)?.toLowerCase() === 'h' ? 'h' : 'c') : 's';

  const {
    data: dashboardMListData,
    isInitialLoading: isInitialLoadingDashboardMListData,
    isFetching: isFetchingDashboardMListData,
  } = useQuery(['dashboardMasterGet', userInfo.hCd, userInfo.sCd], () => fetchDashboardMListData(), {
    enabled: !!userInfo.hCd && !!userInfo.sCd && !!dGubun && !(userInfo.gCd === 'HM' && userInfo.sCd !== '00000'),
  });

  const {
    data: dashboardSetListData,
    isLoading: isLoadingDashboardSetListData,
    isFetching: isFetchingDashboardSetListData,
  } = GetFetchStatus({ path: '/dashboard', req: { dGubun }, queryKey: dGubun });

  const fetchDashboardMListData = async () => {
    try {
      const res = await apiGet({ path: '/dashboard/master', req: { hCd: userInfo.hCd, sCd: userInfo.sCd, dGubun } });
      return res.data.data;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const setPiece = useCallback(
    (dataItemParam: any, bigo: string) => {
      if (dataItemParam) {
        // 유저의 메뉴 권한에 따른 대시보드 노출처리 함수
        if (bigo) {
          // 비고값이 있는경우, 권한유무에 따라 처리
          const authArray = bigo.split(',');
          const mtCd = authArray[0];
          const mdCd = authArray[1];
          const mrCd = authArray[2];
          const hasMenuAuth = userInfo.userMenuList?.find((el: any) => el.mtCd === mtCd && el.mdCd === mdCd && el.mrCd === mrCd);
          if (hasMenuAuth !== undefined) return true;
          return false;
        }
        return true; // 비고값이 없으면 전체공개하는 대시보드
      }
      return false;
    },
    [userInfo.userMenuList]
  );

  useEffect(() => {
    if (dashboardSetListData && dashboardMListData) {
      const { DashboardSetList } = dashboardSetListData;
      const { DashboardMList } = dashboardMListData;
      const useYDashboard = DashboardSetList.filter((setListEl: IDashboard) => setListEl.useYn === 'Y');
      const result = useYDashboard.map((setListEl: IDashboard) => {
        const dataItem = DashboardMList.find((mListEl: IDashboardM) => mListEl.dCd === setListEl.dCd);
        return {
          dCd: setListEl.dCd,
          name: setListEl.dName,
          isInDropZone: setPiece(dataItem, setListEl.bigo),
          // isInDropZone: !!dataItem,
          ...(dataItem ? { location: { column: dataItem.dCol, row: dataItem.dRow } } : { location: { column: -1, row: -1 } }),
          width: setListEl.dCol,
          height: setListEl.dRow,
        };
      });

      const sortedData = result.sort((a: any, b: any) => {
        if (a.location.row !== b.location.row) {
          return a.location.row - b.location.row;
        }
        return a.location.column - b.location.column;
      });
      setDashboardMList(sortedData);
    }
  }, [dashboardMListData, dashboardSetListData, userInfo.userMenuList]);

  useEffect(() => {
    if (dashboardMList?.length > 0) {
      setDragPieces(dashboardMList);
    }
  }, [dashboardMList]);

  useEffect(() => {
    const mergedGrid = dragPieces.map((gridItem: any) => {
      const matchPiece = dragPieces.find((piece) => piece.dCd === gridItem.dCd);
      return matchPiece ? { ...gridItem, ...matchPiece } : gridItem;
    });

    setDropZoneGrid(mergedGrid);
  }, [dragPieces]);

  const handleDrop = (dropItem: Piece, dropCell: any) => {
    const copyDragPieces = [...dragPieces];

    // dropItem의 id와 일치하는 항목을 드래그 피스 배열에서 찾기
    const index = copyDragPieces.findIndex((piece) => piece.dCd === dropItem.dCd);

    // 일치하는 아이템이 있으면 드래그피스 배열 업데이트
    if (index !== -1) {
      // dropItem의 속성을 사용하여 새 항목을 생성하고 InDropZone을 true로 설정
      const updatedItem = {
        ...copyDragPieces[index],
        ...dropItem,
        isInDropZone: true,
        location: dropCell.location,
      };

      // 업데이트된 아이템으로 드래그피스 배열의 아이템 대치
      copyDragPieces[index] = updatedItem;
      setDragPieces(copyDragPieces); // 드래그피스배열 setState
      setReLocatedDragPieces(copyDragPieces);
    }
  };

  useEffect(() => {
    if (reLocatedDragPieces.length === 0) {
      setReLocatedDragPieces(dragPieces);
    }
  }, [dragPieces]);

  const loading = isInitialLoadingDashboardMListData || isLoadingDashboardSetListData || isFetchingDashboardMListData || isFetchingDashboardSetListData;

  return (
    <Root loading={loading} maxRowCount={0} isViewMode={isViewMode}>
      <DndProvider backend={HTML5Backend}>
        <div className='dragNDropZone'>
          <div className='dragZone'>{dragPieces.map((el) => !el.isInDropZone && <DraggableCell key={el.dCd} cell={el} />)}</div>
          <div className='dropZone'>
            {dropZoneGrid?.map(
              (el) =>
                el.isInDropZone && (
                  <DroppableZone
                    key={v1()}
                    onDrop={(item: Piece) => handleDrop(item, el)}
                    cell={el}
                    dragPieces={dragPieces}
                    setDragPieces={setDragPieces}
                    dropZoneGrid={dropZoneGrid}
                    setDropZoneGrid={setDropZoneGrid}
                    isViewMode={isViewMode}
                  >
                    <DraggableCell key={v1()} cell={el} />
                  </DroppableZone>
                )
            )}
          </div>
        </div>
      </DndProvider>
    </Root>
  );
};

export default DashboardSetting;

interface IControl {
  content: any;
}

const ControlStyle = styled.div<any>`
  height: inherit;
  position: relative;
  .tunnel-title {
    display: flex;
    .tunnel-name {
      width: 8rem;
      &.tight {
        width: fit-content;
        margin-right: 1rem;
      }
    }
    .tunnel-guide {
      display: flex;
      align-items: center;
      .seperator {
        margin: 0 1rem;
        border-left: 1px solid var(--outline-em);
        height: 1rem;
        &.tight {
          margin: 0 0.5rem;
        }
      }
    }
  }
  .weatherTodayInfo,
  .weatherWeekInfo {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;

    .weatherError {
      position: absolute;
      justify-content: center;
      align-items: center;
      z-index: 10;
      width: 100%;
      > div {
        width: inherit;
        border-radius: 0.5rem 0.5rem 0 0;

        > p {
          display: flex;
          align-items: center;
          justify-content: center;
          width: inherit;
          padding: 0.25rem 0;
          color: #fff;
          background-color: rgba(0, 0, 0, 0.4);
          border-radius: 0;
          gap: 0.25rem;

          > span.material-symbols-rounded {
            font-size: 20px;
          }
        }
      }
    }

    > .widget-half-background {
      background-color: #3e97ff;
      position: absolute;
      top: 0;
      border-radius: 0.5rem;
      width: 100%;
      height: 50%;
    }
    > .widget-header {
      position: absolute;
      top: 0;
      width: 100%;
      padding: 0.75rem 1rem;
      z-index: 10;
      // background-color: var(--weather-bg);
      > .widget-title {
        // height: 2rem;
        color: white;
        > span {
          font-weight: 600;
          font-size: 1.125rem;
          line-height: 1.2;
        }
      }
      > .widget-subTitle {
      }
    }
    > .widget-body {
      width: 100%;
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      /* overflow: hidden; */
      overflow: visible;
      z-index: 1;
      .weatherToday {
        position: relative;
        flex: 1 0 50%;
        width: 100%;
        max-height: 22rem;
        -webkit-box-pack: justify;
        justify-content: space-around;
        -webkit-box-align: center;
        align-items: center;
        background-color: var(--weather-bg);
        .weatherInfo {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          height: 100%;
          padding: 0 1rem;
          > div:nth-child(1) {
            font-weight: 500;
            font-size: 1.125rem;
          }
        }
        .weatherBox {
          height: 6rem;
          border-radius: 8px;
          padding: 0 1rem;
          text-align: center;
          background: #f3f6f9;
          font-size: 0.875rem;
          dt {
            font-weight: 500;
          }
          dl > div {
            display: flex;
            width: inherit;
            > dt,
            > dd {
              flex: 0.5;
            }
            > dt {
              text-align: start;
            }
            > dd {
              text-align: center;
            }
          }
        }
        .weatherBox.text {
          display: flex;
          flex-direction: column;
          justify-content: center;
          > div {
            display: flex;
            justify-content: space-between;
          }
        }
        .weatherAnimationWrapper {
          .container {
            > div {
              svg {
                pointer-events: none;
              }
            }
          }
        }
      }
      .weatherToday.notWeek .weatherInfo {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        height: 100%;
        z-index: 10;
        padding: 0 1rem;
        > div .weatherBox {
          height: auto;
          background-color: transparent;
          padding: 0;
          > div {
            justify-content: flex-start;
            gap: 0.5rem;
            height: 1.25rem;
          }
        }
      }
      .weatherWeek {
        flex: 1 0 50%;
        padding: 1.25rem 1rem;
        display: flex;
        background-color: var(--board);
        z-index: 999;
        > div {
          flex: 1;
          flex-grow: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: space-between;
          .date {
            display: flex;
            flex-direction: column;
            align-items: center;

            > div {
              font-size: 0.875rem;
              font-weight: 600;
            }
            > div:nth-child(1) {
              color: var(--text-secondary);
            }
            > div:nth-child(2) {
              color: var(--text-primary);
            }
          }
          .weatherWeekIcon {
            > img {
              width: 2.5rem;
            }

            > .no-weather-icon {
              display: flex;
              align-items: center;
              justify-content: center;
            }
          }
          .weatherWeekTemp {
            > div {
              font-weight: 600;
              color: var(--text-secondary);
              font-size: 0.75rem;
            }
          }
        }
      }
      .weatherTodayDetail {
        height: 100%;
        background-color: var(--board);
        z-index: 10;
        .weatherBoxGroup {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.5rem;
          bottom: 1rem;
          position: absolute;
          width: calc(100% - 2rem);
          left: 50%;
          transform: translate(-50%, 0);
          .weatherBox {
            height: 6rem;
            border-radius: 8px;
            padding: 0 1rem;
            text-align: center;
            background: var(--tonal);
            font-size: 0.875rem;
            dt,
            > div > dt,
            > div > dd {
              font-weight: 500;
              color: var(--text-primary);
            }

            dl > div {
              display: flex;
              width: inherit;
              > dt,
              > dd {
                flex: 0.5;
              }
              > dt {
                text-align: start;
              }
              > dd {
                text-align: center;
              }
            }
          }
          .weatherBox.text {
            display: flex;
            flex-direction: column;
            justify-content: center;
            > div {
              display: flex;
              justify-content: space-between;
            }
          }
          .weatherBox.icon {
            display: flex;
            align-items: center;
            justify-content: space-between;
            dt {
              text-align: left;
            }
            > dd {
              position: relative;
              > div:nth-child(1) img {
                height: 4rem;
              }
            }
            .weatherBox-details {
              display: flex;
              flex-direction: column;
              /* gap: 0.5rem; */
              justify-content: center;
              font-size: 0.75rem;
              > div {
                height: 1.25rem;
                font-weight: 600;
                color: var(--text-secondary);
              }
            }
          }
        }
      }
    }

    .contents {
      height: calc(100% - 4rem);
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      > div {
        flex: 0.5;
      }
    }

    .weatherAnimationWrapper {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      overflow: visible;

      .container {
        width: 100%;
        height: inherit;
        position: relative;

        .svg-container {
          left: 0;
        }
      }

      .rotating-sun {
        animation: rotation 10s infinite linear;
        position: absolute;
        top: -1.5rem;
        right: 3.5rem;
      }
      .little_cloud {
        position: absolute;
        right: 1%;
        top: -15%;
        &.beside-sun {
          right: 6%;
        }
      }
      .blur {
        position: absolute;
        right: 8%;
        top: -10%;
        width: 180px;
      }
      .cloudy {
        left: 25% !important;
        top: 17%;
      }
    }
  }

  .snowdrop {
    position: absolute;
    bottom: 0;
    background: transparent;
    animation-name: snowfall;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
    top: -100px;
  }

  @keyframes snowfall {
    0% {
      transform: translateX(0);
      top: -100px;
    }
    100% {
      transform: translateX(var(--random-left-right, 0));
      top: 105%;
    }
  }

  @keyframes fall {
    to {
      transform: translateY(100vh);
    }
  }

  @keyframes rotation {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  .svg-container {
    position: absolute;
    top: 0;
    left: -100%;
    width: inherit;
    height: inherit;
    transition: left 1s ease-in-out;
  }

  .show-svg {
    left: 25%;
  }

  .floating {
    animation: float 3s ease-in-out infinite;
  }

  @keyframes float {
    0% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-10px);
    }
    100% {
      transform: translateY(0);
    }
  }

  .bouncing {
    animation: bounce 4s ease infinite;
  }

  @keyframes bounce {
    0%,
    100% {
      transform: translateY(0);
    }
    14%,
    29% {
      transform: translateY(-20px);
    }
  }

  .raindrop,
  .snowice,
  .sunflower {
    position: absolute;
    bottom: 0;
    opacity: 0.7;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
    top: -100px;
  }

  .raindrop {
    width: 2px;
    height: 15px;
    background: lightgray;
    animation-name: fallRaindrop;
  }

  .snowice {
    width: 10px;
    height: 10px;
    background: transparent;
    animation-name: fallSnowIce;
  }

  .sunflower {
    width: 15px;
    height: 15px;
    background: transparent;
    animation-name: fallSunflower;
  }

  @keyframes fallRaindrop {
    to {
      transform: translateY(100vh);
    }
  }

  @keyframes fallSnowIce {
    to {
      transform: translateY(100vh);
    }
  }

  @keyframes fallSunflower {
    to {
      transform: translateY(100vh);
    }
  }
  > div {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    div.refresh-btn {
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      border-radius: 2rem;
      /* background-color: #f9f9f9; */
      .material-symbols-rounded {
        font-variation-settings: 'FILL' 1, 'wght' 300, 'GRAD' 0, 'opsz' 20;
        color: var(--text-tertiary);
        opacity: 0.6;
      }
    }
    div.refresh-btn:hover {
      background-color: var(--tonal);
      .material-symbols-rounded {
        opacity: 1;
      }
    }
    div.refresh-btn.white {
      .material-symbols-rounded {
        color: white;
        opacity: 0.6;
      }
      /* background-color: rgba(255, 255, 255, 0.1); */
    }
    div.refresh-btn.white:hover {
      .material-symbols-rounded {
        opacity: 1;
      }
      background-color: rgba(255, 255, 255, 0.15);
    }
    div.play-btn {
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 95%;
      border-radius: 0.5rem;
      background-color: var(--tonal);
      .material-symbols-rounded {
        font-size: 10rem;
        font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
        color: var(--text-tertiary);
        opacity: 0.6;
      }
      &.cctv {
        .material-symbols-rounded {
          font-variation-settings: 'FILL' 1;
          font-size: 4rem;
        }
      }
    }
    div.play-btn:hover {
      background-color: var(--tonal-deep);
      .material-symbols-rounded {
        opacity: 1;
      }
    }
    div.play-btn.white {
      .material-symbols-rounded {
        color: white;
        opacity: 0.6;
      }
      /* background-color: rgba(255, 255, 255, 0.1); */
    }
    div.play-btn.white:hover {
      .material-symbols-rounded {
        opacity: 1;
      }
      background-color: rgba(255, 255, 255, 0.15);
    }
  }

  .siteInfo {
    height: 100%;
    position: relative;
    > .widget-header {
      width: 100%;
      padding: 0.75rem 1rem;
      > .widget-title {
        // height: 2rem;
        > span {
          /* font-weight: 600; */
          font-size: 1.125rem;
          line-height: 1.2;
          font-weight: var(--font-semibold);
          color: var(--text-primary);
        }
      }
      > .widget-subTitle {
        font-size: 0.875rem;
        line-height: 1rem;
        color: var(--text-secondary);
        /* color: rgba(0, 0, 0, 0.7); */
      }
    }
    > .widget-body {
      flex-grow: 1;
      width: 100%;
      display: flex;
      flex-direction: row;
      padding: 0 1rem;
      margin-bottom: 1rem;

      .infoBox-container {
        gap: 1rem;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        > .infoBox > .labelAndValue > .infoBox-value {
          margin-right: 0 !important;
        }
      }

      div.infoBox {
        flex: 1 1 25%;
        gap: 1rem;
        > .small {
          width: 2rem !important;
          height: 2rem !important;
          > .material-symbols-rounded {
            font-size: 1.2rem !important;
          }
        }

        > div:nth-child(1) {
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: var(--tonal);
          border-radius: 0.5rem;
          width: 4rem;
          height: 4rem;
          flex-shrink: 0;
          font-size: 1.5rem;
          .material-symbols-rounded {
            font-variation-settings: 'FILL' 1, 'wght' 600, 'GRAD' 200, 'opsz' 48;
            /* color: rgba(0, 0, 0, 0.25); */
            font-size: 1.75rem;
          }
        }
        > div.red {
          /* background-color: #fff5f8; */
          background-color: var(--tonal-red);
        }
        > div.orange {
          color: var(--filled-orange);
          background-color: var(--tonal-orange);
        }
        > div.green {
          /* background-color: #f0fdf4; */
          background-color: var(--tonal-green);
        }
        > div.blue {
          /* background-color: #eef2ff; */
          background-color: var(--tonal-blue);
        }
        > div.violet {
          color: var(--filled-violet);
          background-color: var(--tonal-violet);
        }
        > div.amber {
          color: var(--filled-amber);
          background-color: var(--tonal-amber);
        }
        > div.labelAndValue {
          flex-grow: 1;
          gap: 1rem;
          font-weight: 600;
          height: 100%;
          .infoBox-label {
            color: var(--text-secondary);
            font-weight: 600;
            min-width: 4rem;
          }
          div.infoBox-value {
            display: flex;
            gap: 0.25rem;
            align-items: center;
            color: var(--text-tertiary);
            margin-right: 1rem;
            span {
              font-size: 1.25rem;
              font-weight: 600;
              color: var(--text-primary);
              padding-bottom: 0.125rem;
            }
          }
        }
      }

      .chart-container {
        flex: 0.5;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        .chart-title {
          font-size: 0.75rem;
          color: var(--text-tertiary);
          margin-bottom: 0.5rem;
        }

        .chart-wrapper {
          width: 100%;
          height: 100%;
          max-width: 220px;
          max-height: 220px;
          display: flex;
          position: relative;

          .chart-icon {
            position: absolute;
            top: 52.5%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 8rem;
            height: 8rem;
            border-radius: 8rem;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 10px 0 10px 0 rgba(0, 0, 0, 0.2);
            background-color: var(--board);
            .material-symbols-rounded {
              font-size: 5rem;
              color: var(--filled-blue-light);
            }
          }
        }

        .percentage {
          font-size: 1rem;
          font-weight: 600;
          color: var(--filled-blue-light);
          gap: 0.25rem;
          display: flex;
          align-items: baseline;
          padding: 0.5rem 0.5rem 0 0.5rem;
          line-height: 1;
          margin-top: 0.5rem;

          .emphasize {
            color: var(--filled-blue-light);
            font-size: 3.5rem;
            font-weight: 800;
          }
        }
      }
    }

    @media screen and (max-width: 767px) {
      > .widget-body {
        > :first-child {
          order: 2;
        }
        > :nth-child(2) {
          order: 1;
        }

        flex-direction: column;
        overflow: scroll;
        gap: 1rem;
        align-items: center;
      }
    }
  }

  .totalInfo,
  .safetyViolationInfo {
    height: 100%;
    position: relative;
    > .widget-header {
      width: 100%;
      padding: 0.75rem 1rem;
      > .widget-title {
        // height: 2rem;
        > span {
          /* font-weight: 600; */
          font-size: 1.125rem;
          line-height: 1.2;
          font-weight: var(--font-semibold);
          color: var(--text-primary);
        }
      }
      > .widget-subTitle {
        font-size: 0.875rem;
        line-height: 1rem;
        color: var(--text-secondary);
        /* color: rgba(0, 0, 0, 0.7); */
      }
    }
    > .widget-body {
      flex-grow: 1;
      width: 100%;
      display: flex;
      flex-direction: column;
      padding: 0 1rem;
      margin-bottom: 1rem;
      > div.infoBox {
        flex: 1 1 25%;
        gap: 1rem;
        > div:nth-child(1) {
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: var(--tonal);
          border-radius: 0.5rem;
          width: 4rem;
          height: 4rem;
          flex-shrink: 0;
          font-size: 1.5rem;
          .material-symbols-rounded {
            font-variation-settings: 'FILL' 1, 'wght' 600, 'GRAD' 200, 'opsz' 48;
            /* color: rgba(0, 0, 0, 0.25); */
            font-size: 1.75rem;
          }
        }
        > div.red {
          /* background-color: #fff5f8; */
          background-color: var(--tonal-red);
        }
        > div.orange {
          color: #fb923c;
          fill: #fb923c;
          /* background-color: #fff7ed; */
          background-color: var(--tonal-orange);
        }
        > div.green {
          /* background-color: #f0fdf4; */
          background-color: var(--tonal-green);
        }
        > div.blue {
          /* background-color: #eef2ff; */
          background-color: var(--tonal-blue);
        }
        > div.labelAndValue {
          flex-grow: 1;
          gap: 1rem;
          font-weight: 600;
          height: 100%;
          .infoBox-label {
            color: var(--text-secondary);
            font-weight: 600;
          }
          div.infoBox-value {
            display: flex;
            gap: 0.25rem;
            align-items: center;
            color: var(--text-tertiary);
            margin-right: 1rem;
            span {
              font-size: 1.25rem;
              font-weight: 600;
              color: var(--text-primary);
              padding-bottom: 0.125rem;
            }
          }
        }
      }
      /* > div.infoBox:nth-last-child(n + 2) > div.labelAndValue {
        border-bottom: 1px solid var(--outline);
      } */
    }
    > .widget-body.safety {
      user-select: none;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      > div {
        gap: 0.75rem;
        > div:nth-child(1) {
          width: 4.5rem;
          height: 4.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.025);
          border-radius: 0.5rem;
          .material-symbols-rounded {
            font-variation-settings: 'FILL' 1, 'wght' 600, 'GRAD' 200, 'opsz' 40;
            font-size: 2rem;
          }
        }
        > div.red {
          background-color: var(--tonal-red);
        }
        > div.orange {
          color: #fb923c;
          fill: #fb923c;
          background-color: var(--tonal-orange);
        }
        > div:nth-child(2) {
          font-size: 0.875rem;
          line-height: 1.25rem;
          color: var(--text-secondary);
          font-weight: var(--font-semibold);

          > div {
            font-size: 1rem;
            span {
              font-size: 1.25rem;
              color: var(--text-primary);
              font-weight: var(--font-semibold);
            }
          }
        }
      }
    }
  }

  .pmInfo,
  .cctvNarrow {
    li {
      width: 100% !important;
      max-width: none !important;
    }
  }

  .mGas {
    overflow: visible;
    > .widget-header {
      position: sticky;
      top: 0;
      z-index: 1;
      background-color: var(--board);
    }
  }

  .attend,
  .attendTotalInfo,
  .equipInfo,
  .oldWorker,
  .newWorker,
  .pmInfo,
  .dDay,
  .accidentInfo,
  .cctvWide,
  .cctvNarrow,
  .safetyViolationInfoDetail,
  .noxiousGas,
  .mGas,
  .imageBoard {
    user-select: none;
    height: 100%;
    > .widget-header {
      width: 100%;
      padding: 0.75rem 0;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      &.margin-top {
        margin-bottom: 0;
      }
      > .widget-title {
        padding: 0 1rem;
        align-items: center;
        > span {
          flex-shrink: 0;
          font-weight: var(--font-semibold);
          font-size: 1.125rem;
          line-height: 1.2;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          > .badge {
            font-weight: 500;
            font-size: 0.875rem;
            padding: 0.125rem 0.5rem;
            border-radius: 0.25rem;
            background-color: var(--tonal-blue);
            margin: 0 0.5rem;
            color: rgb(62, 151, 255);
            display: inline-block;
            min-width: 1.75rem;
            text-align: center;
          }
        }
        > .widget-top-btn-wrap {
          display: flex;
          align-items: center;
          gap: 0.5rem;

          > .widget-btn-group {
            display: flex;
            gap: 0.5rem;
            align-items: center;
            > span {
              font-weight: var(--font-semibold);
              font-size: 1.125rem;
              color: var(--text-primary);
            }
            > button {
              height: 2rem;
              font-weight: var(--font-bold);
              padding: 0 0.75rem;
              border-radius: 1rem;
              font-size: 0.875rem;
              color: var(--text-secondary);
              background-color: var(--tonal);
              border-radius: 0.5rem;
              gap: 0;
            }
            > button:hover {
              background-color: var(--tonal);
            }
            > button:nth-child(1) {
              padding-left: 0.25rem;
              padding-right: 0.5rem;
              .material-symbols-rounded {
                font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 20;
                color: #78829d;
              }
            }
            > .innerTab {
              gap: 0.25rem;
              > div {
                cursor: pointer;
                height: 2rem;
                padding: 0 0.75rem;
                display: flex;
                align-items: center;
                font-weight: 500;
                color: var(--text-secondary);
                border-radius: 1rem;
                font-weight: var(--font-medium);
                /* font-size: 0.875rem; */
              }
              > div.active {
                font-weight: var(--font-bold);
                color: #3e97ff;
                background-color: var(--tonal);
              }
              > div:hover {
                background-color: var(--tonal);
              }
            }
          }
        }
        > .widget-title-innerTab,
        > .widget-btn-group {
          display: flex;
          gap: 0.5rem;
          align-items: center;
          > span {
            font-weight: var(--font-semibold);
            font-size: 1.125rem;
            color: var(--text-primary);
          }
          > button {
            height: 2rem;
            font-weight: var(--font-bold);
            padding: 0 0.75rem;
            border-radius: 1rem;
            font-size: 0.875rem;
            color: var(--text-secondary);
            background-color: var(--tonal);
            border-radius: 0.5rem;
            gap: 0;
          }
          > button:hover {
            background-color: var(--tonal);
          }
          > button:nth-child(1) {
            padding-left: 0.25rem;
            padding-right: 0.5rem;
            .material-symbols-rounded {
              font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 20;
              color: #78829d;
            }
          }
          > .innerTab {
            gap: 0.25rem;
            > div {
              cursor: pointer;
              height: 2rem;
              padding: 0 0.75rem;
              display: flex;
              align-items: center;
              color: var(--text-secondary);
              border-radius: 1rem;
              font-weight: var(--font-medium);
              /* font-size: 0.875rem; */
            }
            > div.active {
              font-weight: var(--font-bold);
              color: #3e97ff;
              background-color: var(--tonal);
            }
            > div:hover {
              background-color: var(--tonal);
            }
          }
        }
      }
      > .widget-subTitle {
      }
    }
    .widget-header.safety {
      gap: 0;
      .widget-subTitle {
        padding: 0 1rem;
        font-size: 0.875rem;
        line-height: 1rem;
        color: var(--text-secondary);
      }
    }
    .widget-body {
      display: flex;
      flex-direction: column;
      width: 100%;
    }
    .widget-body.photoTable {
      flex-grow: 1;
      overflow: hidden;
      > .innerTab {
        width: 100%;
        display: flex;
        border-bottom: 1px solid var(--outline);
        > div {
          height: 3rem;
          display: flex;
          align-items: center;
          flex: 1 1 50%;
          justify-content: center;
          border-bottom: 2px solid transparent;
          font-size: 0.875rem;
          cursor: pointer;
          color: var(--text-secondary);
          font-weight: 700;
        }
        > div.active {
          color: var(--text-primary);
          border-bottom: 2px solid var(--selected-primary);
        }
      }
      > .sliderWrapper {
        width: 100%;
        height: 100%;
        overflow-y: scroll;
        overflow-x: hidden;
        position: relative;
        ::-webkit-scrollbar-thumb {
          background-color: transparent;
        }
        &:hover::-webkit-scrollbar-thumb {
          background-color: var(--scrollbar);
        }
        .text-container {
          display: flex;
          flex-direction: column;
          position: relative;
        }

        .text-container .active {
          animation: slideIn 2s forwards;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slide {
          0% {
            transform: translateY(0);
          }
          100% {
            transform: translateY(-100%);
          }
        }

        .rowWrapper {
          width: 100%;
          padding-right: 0.5rem;
          padding-left: 1rem;
          cursor: pointer;
          gap: 0.5rem;
          height: 3.5rem;
          > div:nth-child(1) {
            flex-grow: 1;
            gap: 0.5rem;
            width: 12rem;
            .imgWrapper {
              width: 2.25rem;
              height: 2.25rem;
              border-radius: 0.25rem;
              overflow: hidden;
              flex-shrink: 0;
              display: flex;
              align-items: center;
              justify-content: center;
              background-color: rgba(0, 0, 0, 0.05);
              .material-symbols-rounded {
                font-variation-settings: 'FILL' 1, 'wght' 600, 'GRAD' 200, 'opsz' 48;
                color: rgba(0, 0, 0, 0.25);
                font-size: 1.75rem;
              }

              img {
                width: inherit;
                height: inherit;
                object-fit: cover;
              }
            }
            > div:nth-child(2) {
              width: calc(100% - 2.75rem);
              > div:nth-child(1),
              > .rowContent {
                width: 100%;
                white-space: nowrap;
                text-overflow: ellipsis;
                overflow: hidden;
                font-size: 0.875rem;
                line-height: 1.25rem;
                height: 1.25rem;
                color: var(--text-disabled);
                span {
                  color: var(--text-primary);
                  font-size: 0.875rem;
                  line-height: 1rem;
                }
              }
            }
          }
        }
        .rowWrapper:hover {
          background-color: var(--tonal);
        }
        .buttonsWrapper {
          button {
            height: inherit;
            padding: 0;
            width: 2.5rem;
            font-size: 0.875rem;
            height: 1.75rem;
            span {
              font-weight: 700;
              margin: 0 0.125rem;
            }
          }
          button.red {
            background-color: var(--tonal-red);
            color: var(--filled-red);
          }
          button.amber {
            background-color: var(--tonal-orange);
            color: var(--filled-orange);
          }
        }
      }
    }
    .widget-body.chart {
      width: 100%;
      flex-grow: 1;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      .doughnutChartWrapper {
        height: inherit;
        > canvas {
          width: 10rem;
          height: 10rem;
          margin: 0 auto;
        }
      }
      .content {
        margin: 1rem 0;
        display: flex;
        align-items: center;
        gap: 1rem;
        dl {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          color: var(--text-secondary);
          > .chart-color {
            width: 0.75rem;
            height: 0.75rem;
            border-radius: 1rem;
          }
          > .chart-color.blue {
            background-color: var(--primary);
          }
          > .chart-color.green {
            background-color: var(--filled-green);
          }
          > .chart-color.red {
            background-color: var(--filled-red);
          }
          > span > span.value {
            font-weight: 600;
            color: var(--text-primary);
          }
        }
      }
    }
    .widget-body.progressBar {
      width: 100%;
      flex-grow: 1;
      padding: 0 1rem;
      justify-content: space-between;
      margin-bottom: 1rem;
      > .progressBar-group {
        display: flex;
        flex-direction: column;
        > .progressBar-text {
          > span:nth-child(1) {
            color: var(--text-secondary);
          }
          > span:nth-child(2) {
            color: var(--text-primary);
          }
        }
        .fullBar {
          height: 0.75rem;
          border-radius: 1rem;
          margin-top: 0.25rem;
          overflow: hidden;
          display: flex;
          .fillBar {
            background-color: rgb(80, 205, 137);
            height: 100%;
          }
          .emptyBar {
            background-color: rgb(80, 205, 137);
            height: 100%;
            opacity: 0.35;
          }
        }
      }
      .content {
        width: 100%;
        font-size: 0.875rem;
        dl {
          gap: 0.5rem;
          > dt {
            min-width: 4rem;
          }
          > dt {
            color: var(--text-secondary);
            font-weight: 600;
          }
          > .badge {
            padding: 0.125rem 0.375rem;
            color: #3e97ff;
            background-color: var(--tonal-blue);
            border-radius: 0.25rem;
            line-height: 1rem;
          }
        }
        dd {
          color: var(--text-primary);
        }
        > div {
          > span {
            border-left: 1px solid var(--outline);
            padding: 0.5rem 0;
            margin: 0 0.5rem;
          }
          span.green {
            padding: 0.125rem 0.375rem;
            background-color: var(--tonal-green);
            border-radius: 0.25rem;
          }
          span.blue {
            padding: 0.125rem 0.375rem;
            background-color: var(--tonal-blue);
            border-radius: 0.25rem;
          }
        }
      }
    }
    .widget-body.donut {
      width: 100%;
      flex-direction: row;
      > .doughnutChartWrapper {
        overflow: hidden;
        height: 7rem;
        padding: 0 1rem;
        margin-bottom: 1rem;
        width: 9rem;
        flex-shrink: 0;
      }
      > .content {
        flex: 1 1 auto;
        padding-right: 1rem;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        dd,
        dt {
          font-size: 0.875rem;
          color: var(--text-primary);
        }
        dl {
          gap: 0.5rem;
          > dt {
            color: var(--text-secondary);
            font-weight: 600;
            gap: 0.5rem;
            div {
              width: 0.75rem;
              height: 0.75rem;
              border-radius: 1rem;
            }
            div.fill {
              background-color: #3e97ff;
            }
            div.empty {
              background-color: #3e97ff;
              opacity: 0.25;
            }
          }
          > dd {
            gap: 0.25rem;
            span {
              padding: 0.125rem 0.375rem;
              color: #3e97ff;
              background-color: var(--tonal-blue);
              border-radius: 0.25rem;
              line-height: 1rem;
            }
          }
        }
      }
    }
    .widget-body.table {
      width: 100%;
      flex-grow: 1;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      overflow: hidden;
      > div {
        height: 100%;
        .table {
          flex-grow: 1;
        }
      }
      .tr {
        pointer-events: none;
        .padding-right {
          padding-right: 1rem;
        }
        .tableStickyNo {
          padding-left: 1rem;
          font-weight: 600;
        }
        span.redLight {
          width: 2.125rem;
          height: 1.5rem;
          color: var(--filled-red);
          font-weight: 600;
          text-align: center;
          border-radius: 0.25rem;
          background-color: var(--tonal-red);
        }
        span.orangeLight {
          width: auto;
          height: 1.5rem;
          padding: 0 0.25rem;
          color: var(--filled-orange);
          font-weight: 600;
          text-align: center;
          border-radius: 0.25rem;
          background-color: var(--tonal-orange);
        }
        span.greenLight {
          width: 2.125rem;
          height: 1.5rem;
          color: var(--filled-green);
          font-weight: 600;
          text-align: center;
          border-radius: 0.25rem;
          background-color: var(--tonal-green);
        }
      }
      .tr.hover {
      }
    }
    .widget-body.graph {
      width: 100%;
      height: 100%;
      padding: 0 1rem;
      > div:nth-child(1) {
        width: 100%;
        > ul > li {
          width: 100%;
          max-width: none;
          > span {
            width: 100%;
          }
        }
      }
      > .lineChartWrapper {
        padding-bottom: 1rem;
        height: 100%;
      }
    }
    .widget-body.cctv-narrow {
      flex-grow: 1;
      padding: 0 1rem;
      margin-bottom: 1rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      > div:nth-child(1) {
        > ul > li {
          width: 100%;
          max-width: none;
          span {
            width: 100%;
          }
        }
      }
      .cctvWrapper {
        border-radius: 0.25rem;
        padding: 0;
      }
      .cameraLabel {
        display: none;
      }
      .controllerWrapper {
        display: none;
      }
    }
    .widget-body.cctv-wide {
      flex-direction: row;
      gap: 0.5rem;
      padding: 0 1rem;
      height: 100%;
      overflow: hidden;
      > div:nth-child(2) {
        flex-grow: 1;
      }
      .contents {
        padding: 0rem 0.75rem;
      }

      .cctvWrapper {
        border-radius: 0.25rem;
        padding: 0;
        > div {
          border-radius: 0;
        }
      }
      .controllerWrapper {
        border-radius: 0;
        background-color: transparent;
        .leftControl,
        .rightControl {
          display: none;
        }
        justify-content: center;
        .controllerBtn {
          padding: 0;
          width: 2rem;
          height: 2rem;
          color: var(--text-secondary);
        }
        .controllerBtn.activePage {
          border: none;
          font-weight: 500;
          color: var(--text-primary);
          background-color: var(--tonal-deep);
        }
      }
      .camListWrapper {
        display: flex;
        flex-direction: column;
        overflow: auto;
        flex-shrink: 0;
        margin-bottom: 1rem;
        background-color: var(--tonal);
        border-radius: 0.25rem;
        ::-webkit-scrollbar-thumb {
          background-color: transparent;
        }
        &:hover::-webkit-scrollbar-thumb {
          background-color: var(--scrollbar);
        }
        > button {
          height: 2.5rem;
          width: 10rem;
          padding: 0px 1rem;
          font-size: 0.875rem;
          justify-content: flex-start;
          flex-shrink: 0;
          font-weight: 500;
          display: block;
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
          text-align: left;
          /* &:hover {
            background-color: transparent;
          } */
        }
        .active {
          background-color: transparent;
          border: none;
          color: var(--text-secondary);
        }
      }

      .controllerWrapper {
        padding-bottom: 0.5rem;
      }
    }
    .tag {
      font-size: 0.875rem !important;
    }

    &.blue {
      background-color: var(--primary);
    }
    &.green {
      background-color: var(--filled-green);
    }
    &.red {
      background-color: var(--filled-red);
    }
    &.amber {
      background-color: var(--filled-amber);
    }
    &.teal {
      background-color: var(--filled-teal);
    }
    &.violet {
      background-color: var(--filled-violet);
    }
    &.pink {
      background-color: var(--filled-pink);
    }
    &.indigo {
      background-color: var(--filled-indigo);
    }
    &.sky {
      background-color: var(--filled-sky);
    }
    &.lime {
      background-color: var(--filled-lime);
    }
    &.orange {
      background-color: var(--filled-orange);
    }
  }
  .cctv {
    .cctvWrapper,
    .controllerWrapper {
      max-height: 22rem;
      max-width: inherit;
    }
    .contents {
      > :first-child {
        padding-bottom: 1rem;
      }
    }
  }
  .pieChartWrapper {
    height: 100%;
    width: 100%;
    align-items: center;
    justify-content: space-around;
    padding: 0 2rem;
    gap: 1rem;
    margin-bottom: 2rem;
    > div {
      flex: 1 1 50%;
      height: 100%;
      display: flex;
      flex-direction: column;
      > div.text-center {
        font-weight: var(--font-medium);
        color: var(--text-primary);
        & > span {
          border-radius: 0.5rem;
          background-color: var(--tonal-blue);
          color: #3e97ff;
          margin: 0 0.25rem;
          padding: 0.25rem 0.5rem;
        }
      }
      > div.pie-group {
        display: flex;
        gap: 1rem;
        height: 100%;
        > div.pie {
          width: 8rem;
          canvas {
          }
        }
        > div.legends {
          flex: 1 0 25%;
          -webkit-box-align: center;
          align-items: flex-start;
          justify-content: center;
          height: 100%;
          font-size: 0.875rem;
          > div {
            color: var(--text-tertiary);
            display: flex;
            align-items: center;
            &.legend:nth-child(n + 7) {
              display: none;
            }
            span.text_primary {
              margin-left: 0.25rem;
              font-weight: 600;
            }
            div {
              border-radius: 10rem;
              width: 0.75rem;
              height: 0.75rem;
              margin-right: 0.25rem;
              &.blue {
                background-color: var(--primary);
              }
              &.green {
                background-color: var(--filled-green);
              }
              &.red {
                background-color: var(--filled-red);
              }
              &.amber {
                background-color: var(--filled-amber);
              }
              &.teal {
                background-color: var(--filled-teal);
              }
              &.violet {
                background-color: var(--filled-violet);
              }
              &.pink {
                background-color: var(--filled-pink);
              }
              &.indigo {
                background-color: var(--filled-indigo);
              }
              &.sky {
                background-color: var(--filled-sky);
              }
              &.lime {
                background-color: var(--filled-lime);
              }
              &.orange {
                background-color: var(--filled-orange);
              }
            }
            span.name {
              white-space: nowrap;
              text-overflow: ellipsis;
              overflow: hidden;
              max-width: 7rem;
            }
            span.count {
              margin-left: 0.5rem;
              margin-right: 0.125rem;
              color: var(--text-primary);
              font-weight: var(--font-bold);
            }
          }
        }
      }
    }
  }
  .lineChartWrapper {
    height: 280px;
  }
  .barChartWrapper {
    height: 100%;
    width: 100%;
    padding: 1rem;
  }
  .tuiGridWrapper {
    width: 100%;
    &.scroll {
      .tui-grid-body-area {
        overflow-x: scroll;
      }
    }

    &.noScroll {
      /* .tui-grid-body-container {
        width: 100% !important;
        .tui-grid-table-container {
          width: 100% !important;
        }
      } */
      .tui-grid-body-area {
        overflow-x: hidden;
      }
    }
  }

  .noxiousGas,
  .excavationProcess,
  .mGas {
    .tui-grid-content-area {
      > .tui-grid-lside-area {
        /* display: none !important;
        width: 0 !important; */
      }

      > .tui-grid-rside-area {
        /* margin-left: 0 !important; */
        > .tui-grid-body-area > .tui-grid-body-container > .tui-grid-table-container > .tui-grid-table > tbody > tr > td > div > span {
          padding: 0.5rem;
          /* border-radius: 5px; */
          border-radius: 1rem;
        }
      }

      .tui-grid-cell-content > span {
        border-radius: 5px;
        padding: 0.25rem 0.75rem !important;
      }
    }

    .labelSet {
      /* margin: 0 1rem; */
      flex-grow: 1;
      > span {
        padding: 0 0.25rem !important;
        margin-left: 0.25rem;
      }
    }

    .textBox {
      /* border-radius: 5px; */
      border-radius: 1rem;
      padding: 0 0.2rem !important;
      margin-left: 0.125rem;
      font-size: 0.875rem;
      word-break: keep-all;
    }

    .blue {
      color: var(--primary);
      background-color: var(--tonal-blue);
    }

    .red {
      color: var(--filled-red);
      background-color: var(--tonal-red);
    }

    .violet {
      color: var(--filled-violet);
      background-color: var(--tonal-violet);
    }

    .orange {
      color: var(--filled-orange);
      background-color: var(--tonal-orange);
    }

    .green {
      color: var(--filled-green);
      background-color: var(--tonal-green);
    }

    .amber {
      color: var(--filled-amber);
      background-color: var(--tonal-amber);
    }

    .slate {
      color: var(--filled-slate);
      background-color: var(--tonal);
    }
  }

  .excavationProcess,
  .mGas {
    .title,
    .content {
      width: 30%;
    }

    .textBox {
      border-radius: 5px;
      padding: 0 0.5rem;
    }

    .table .table > .tbody > .tr > * {
      height: 1.2rem;
      margin: 0.5rem 0;
    }
    /* CSS for the progress container */
    .progress-container {
      // width: 100%;
      height: 1.2rem;
      border: 0;
      position: relative;

      .progressValue {
        position: relative;
        height: inherit;
        top: -26px;
        color: #fff;
      }
    }

    /* Styling the progress bar */
    progress {
      width: 100%;
      height: 100%;
      appearance: none;
      border-radius: 15px;
      background-color: rgba(80, 205, 137, 0.15);
    }

    /* Styling the uncompleted progress bar */
    progress::-webkit-progress-bar {
      background-color: rgba(80, 205, 137, 0.15); /* Uncompleted progress color */
      border-radius: 15px; /* Apply the same border radius as the progress element */
    }

    /* Styling the value (completed progress) */
    progress::-webkit-progress-bar {
      border-radius: 15px;
    }

    /* Styling the value (completed progress) */
    progress::-webkit-progress-value {
      background-color: rgba(80, 205, 137, 1);
      border-radius: 15px;
    }

    /* Styling the text inside the progress bar */
    .progress-text {
      text-align: center;
      font-size: 14px;
      line-height: 20px;
      color: white;
      background-color: #4caf50;
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
    }
  }

  .mGas {
    .thead {
      border-bottom: none;
      > :last-child {
        > div {
          border-right: 1px solid var(--outline-em);
        }
      }
      > .tr {
        border-bottom: 1px solid var(--outline);

        > div {
          padding: 0.5rem;
          text-align: center;
        }
      }
    }

    > .table {
      .thead {
        border-top: 1px solid var(--outline-em);
      }
      > div {
        padding: 1rem 0;
      }
    }

    .mGasColumn {
      &.stickyTd {
        position: sticky;
        left: 0;
        background-color: var(--board);
      }
      > div {
        border-bottom: 1px solid var(--outline);
        height: 2.5rem;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
      }
    }
    .textLight {
      text-align: center;
      min-width: 3rem;
      font-size: 0.875rem;
      padding: 0 0.25rem;
      border-radius: 0.25rem;
    }
    .light {
      display: block;
      border-radius: 50%;
      text-align: center;
      font-size: 1.5rem;
      &.blue {
        color: var(--primary);
        background-color: transparent;
      }
      &.green {
        color: var(--filled-green);
        background-color: transparent;
      }
      &.orange {
        color: var(--filled-orange);
        background-color: transparent;
      }
      &.red {
        color: var(--filled-red);
        background-color: transparent;
      }
    }
  }

  .emptyData {
    width: 100%;
    height: calc(100% - 4rem);
    img {
      width: inherit;
      height: inherit;
      object-fit: contain;
      padding: 3rem;
      -webkit-user-drag: none;
    }
    .main_text {
      padding: 1rem;
      background-color: var(--tonal);
      border-radius: 0.5rem;
      margin: 0 1rem;
      .font_semibold {
        margin-left: 0.25rem;
      }
      .seperator {
        margin: 0 0.5rem;
        border-left: 1px solid var(--outline-em);
      }
    }
  }

  @media screen and (max-width: 767px) {
    .widget-title-innerTab {
      flex-direction: column;
      align-items: flex-start !important;
      .innerTab {
        overflow: scroll;
        ::-webkit-scrollbar {
          height: 0rem;
        }
        > div {
          min-width: fit-content;
        }
      }
    }

    .camListWrapper {
      display: none !important;
    }

    .pieChartWrapper {
      flex-direction: column;
      overflow: scroll;
      justify-content: flex-start;
    }

    .mobile_disabled {
      display: none !important;
    }
  }

  .mGas.tempEdit {
    .widget-header {
      padding-bottom: 0.5rem !important;
    }
  }

  .imageBoard {
    height: 100%;
    .content {
      padding: 0;
      margin: 0;
      width: 100%;
      height: calc(100% - 3.5rem);
      flex-grow: 1;
      > div {
        height: 100%;
        .slick-list {
          height: inherit;
          div {
            height: 100%;
          }
        }
      }

      > div > div > div > div > div > img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
        margin: auto;
      }

      .slick-arrow {
        display: none !important;
      }

      .slick-dots {
        top: -2rem;
        width: inherit;
        right: 1rem;
        display: flex !important;
        gap: 0.5rem;
        li {
          margin: 0;
          width: 0.75rem;
          height: 0.75rem;
          border-radius: 0.25rem;
          button {
            width: 0.75rem;
            width: 24px;
          }
          button:before {
            transition: all ease-in-out 0.2s;
            font-family: 'slick';
            font-size: 15px;
            line-height: 20px;
            position: absolute;
            top: 0;
            left: 0;
            width: 12px;
            height: 12px;
            border-radius: 0.25rem;
            content: '';
            text-align: center;
            color: black;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            background-color: rgb(62, 151, 255);
          }
        }

        .slick-active {
          button:before {
            width: 16px;
            width: 24px;
          }
        }
      }
    }
  }
`;

const Control = ({ content }: IControl) => {
  const { dCd, name, isInDropZone } = content && content.props.cell;
  const userInfo = useRecoilValue(userState);
  const theme = useRecoilValue(themeState);
  const [openModal, setOpenModal] = useState<any>({ status: false, type: '', code: '', index: 0 });
  const [currentDate, setCurrentDate] = useState(new Date());

  const handleStateChange = (v: any) => {
    setOpenModal(v);
  };

  useEffect(() => {
    if (cctv.getPluginOBJECT()?.oPlugin) {
      if (openModal.status) {
        cctv.hideWndAll();
      } else {
        cctv.showWndAll();
      }
    }
  }, [openModal]);

  const {
    scheduleList,
    isLoading: isLoadingSchedules,
    refetchSchedule,
    queryClient,
    headInfoList,
    scheduleInfoList,
    companyUser,
    userPartList,
    carInfoList,
    isLoading,
    isRefetchingSchedules,
    auth,
  } = useCalendar({ currentDate, setCurrentDate, enabled: dCd === '098' || dCd === '099' });

  return (
    <ControlStyle dCd={dCd}>
      {
        <>
          {/* 기상정보 */}
          {dCd === '001' && isInDropZone && <WeatherInfo name={name} />}
          {/* 기상 정보 (주간) */}
          {dCd === '002' && isInDropZone && <WeatherWeekInfo />}
          {/* 종합 현황 */}
          {dCd === '003' && isInDropZone && <TotalInfo name={name} userInfo={userInfo} uniqueKey={dCd} />}
          {/* 종합 현황 (신규/금일 출역/퇴장/잔류) */}
          {dCd === '151' && isInDropZone && <TotalInfo name={name} userInfo={userInfo} uniqueKey={dCd} />}
          {/* 신규 근로자 */}
          {dCd === '004' && isInDropZone && <NewWorker name={name} openModal={openModal} setOpenModal={handleStateChange} dCd={dCd} />}
          {/* 고령 근로자 */}
          {dCd === '005' && isInDropZone && <OldWorker name={name} openModal={openModal} setOpenModal={handleStateChange} dCd={dCd} />}
          {/* 출역 정보 */}
          {dCd === '006' && isInDropZone && <AttendInfo name={name} theme={theme} />}
          {/* 출역 종합 정보 */}
          {dCd === '007' && isInDropZone && <AttendTotalInfo name={name} />}
          {/* 출입 장비 연결 정보 */}
          {dCd === '008' && isInDropZone && <EquipInfo name={name} />}
          {/* 현장 CCTV (소형) */}
          {dCd === '009' && isInDropZone && <Cctv openModal={openModal} setOpenModal={handleStateChange} />}
          {/* 현장 CCTV (대형) */}
          {dCd === '010' && isInDropZone && <CctvWide openModal={openModal} setOpenModal={handleStateChange} />}
          {/* 미세먼지 측정기 */}
          {dCd === '013' && isInDropZone && <PmInfo name={name} sensorCd='01' />}
          {/* 소음 측정기 */}
          {dCd === '014' && isInDropZone && <PmInfo name={name} sensorCd='02' />}
          {/* 질소 측정기 */}
          {dCd === '015' && isInDropZone && <PmInfo name={name} sensorCd='03' />}
          {/* 풍속 측정기 */}
          {dCd === '016' && isInDropZone && <PmInfo name={name} sensorCd='04' />}
          {/* 산소 측정기 */}
          {dCd === '017' && isInDropZone && <PmInfo name={name} sensorCd='05' />}
          {/* 가스 측정기 */}
          {dCd === '018' && isInDropZone && <PmInfo name={name} sensorCd='06' />}
          {/* 진동 측정기 */}
          {dCd === '100' && isInDropZone && <PmInfo name={name} sensorCd='10' />}
          {/* 기울기 측정기 */}
          {dCd === '119' && isInDropZone && <PmInfo name={name} sensorCd='11' />}
          {/* 균열 측정기 */}
          {dCd === '120' && isInDropZone && <PmInfo name={name} sensorCd='12' />}
          {/* D-DAY */}
          {dCd === '019' && isInDropZone && <Dday name={name} userInfo={userInfo} />}
          {/* 신규 고령 근로자 */}
          {dCd === '020' && isInDropZone && <NewOldWorker name={name} openModal={openModal} setOpenModal={handleStateChange} dCd={dCd} />}
          {/* 안전 위반 현황 */}
          {dCd === '025' && isInDropZone && <SafetyViolationInfo name={name} userInfo={userInfo} />}
          {/* 무사고 현황 */}
          {dCd === '027' && isInDropZone && <AccidentInfo name={name} userInfo={userInfo} />}
          {/* 안전 위반 현황 상세 */}
          {dCd === '029' && isInDropZone && <SafetyViolationInfoDetail name={name} userInfo={userInfo} openModal={openModal} setOpenModal={handleStateChange} />}
          {/* 근로자 출역 현황 */}
          {dCd === '033' && isInDropZone && <WorkerAttend openModal={openModal} setOpenModal={handleStateChange} showImg dCd={dCd} showForeignWorkerOnly={false} />}
          {/* 외국인 근로자 출역 현황 */}
          {dCd === '118' && isInDropZone && <WorkerAttend openModal={openModal} setOpenModal={handleStateChange} showImg dCd={dCd} showForeignWorkerOnly />}
          {/* 고령 근로자 출역 현황 */}
          {dCd === '034' && isInDropZone && <OldWorkerAttend name={name} openModal={openModal} setOpenModal={handleStateChange} dCd={dCd} />}
          {/* 유해가스 현황 (도형, 대형) */}
          {dCd === '035' && isInDropZone && <MGasInfo size='large' valueType='shape' />}
          {/* 굴진 진행 현황 */}
          {dCd === '037' && isInDropZone && <ExcavationProcess />}
          {/* 굴진 진행 현황 */}
          {dCd === '042' && isInDropZone && <ExcavationProcessLarge />}
          {/* 근로자 위치 현황 */}
          {dCd === '039' && isInDropZone && <ExcavationWorkerPosition name={name} />}
          {/* 유해가스 현황 (도형, 소형) */}
          {dCd === '041' && isInDropZone && <MGasInfo size='small' valueType='shape' />}
          {/* 유해가스 현황 (숫자, 대형) */}
          {dCd === '044' && isInDropZone && <MGasInfo size='large' valueType='number' />}
          {/* 유해가스 현황 (숫자, 소형) */}
          {dCd === '046' && isInDropZone && <MGasInfo size='small' valueType='number' />}
          {/* 터널 종합 진행 현황(대형) */}
          {dCd === '047' && isInDropZone && <TunnelTotalInfo size='large' uniqueKey={dCd} />}
          {/* 터널 종합 진행 현황 */}
          {dCd === '048' && isInDropZone && <TunnelTotalInfo uniqueKey={dCd} />}
          {/* 터널 구역별 종합 진행 현황(대형) */}
          {dCd === '049' && isInDropZone && <TunnelAreaInfo size='large' uniqueKey={dCd} />}
          {/* 터널 구역별 종합 진행 현황 */}
          {dCd === '050' && isInDropZone && <TunnelAreaInfo uniqueKey={dCd} />}
          {/* 터널 종합 진행 현황(일반) */}
          {dCd === '051' && isInDropZone && <TunnelTotalTable visibleRefreshBtn uniqueKey={dCd} />}
          {/* 기상정보/시간대별(2칸) 79 */}
          {dCd === '079' && isInDropZone && <WeatherTotal combination={{ time: true, week: false }} size='small' autoRefresh={false} />}
          {/* 기상정보/주간(2칸) 80 */}
          {dCd === '080' && isInDropZone && <WeatherTotal combination={{ time: false, week: true }} size='small' autoRefresh={false} />}
          {/* 기상종합(3칸) 81 */}
          {dCd === '081' && isInDropZone && <WeatherTotal combination={{ time: true, week: true }} size='small' autoRefresh={false} />}
          {/* 무선 유해가스 현황 (도형, 대형) */}
          {dCd === '083' && isInDropZone && <WGasInfo autoRefresh={false} size='large' valueType='shape' uniqueKey={dCd} />}
          {/* 무선 유해가스 현황 (도형, 소형) */}
          {dCd === '084' && isInDropZone && <WGasInfo autoRefresh={false} size='small' valueType='shape' uniqueKey={dCd} />}
          {/* 무선 유해가스 현황 (숫자, 대형) */}
          {dCd === '085' && isInDropZone && <WGasInfo autoRefresh={false} size='large' valueType='number' uniqueKey={dCd} />}
          {/* 무선 유해가스 현황 (숫자, 소형) */}
          {dCd === '086' && isInDropZone && <WGasInfo autoRefresh={false} size='small' valueType='number' uniqueKey={dCd} />}
          {/* 근로자 출역 현황(출역사진 제외) */}
          {dCd === '089' && isInDropZone && <WorkerAttend openModal={openModal} setOpenModal={handleStateChange} showImg={false} dCd={dCd} showForeignWorkerOnly={false} />}
          {/* 일정 관리(캘린더) */}
          {dCd === '098' && isInDropZone && !isLoadingSchedules && !isLoading && scheduleList && (
            <BigCalendar
              events={scheduleList}
              refetch={refetchSchedule}
              queryClient={queryClient}
              currentDate={currentDate}
              setCurrentDate={setCurrentDate}
              auth={auth}
              isSearchSmGubunAll
              headInfoList={headInfoList}
              scheduleInfoList={scheduleInfoList}
              userPartList={userPartList}
              carInfoList={carInfoList}
              companyUser={companyUser}
              hideOptionalToolBar
              isLoading={isRefetchingSchedules}
            />
          )}
          {/* 현장 조감도 */}
          {dCd === '101' && isInDropZone && <SiteView name={name} userInfo={userInfo} />}
          {/* 현장 정보 */}
          {dCd === '102' && isInDropZone && <SiteInfo name={name} userInfo={userInfo} />}
          {/* 기상종합 대형 ( 가로 4, 세로 2 ) */}
          {dCd === '103' && isInDropZone && <WeatherTotal combination={{ time: true, week: false }} size='large' autoRefresh={false} />}
          {/* 방문자 출입현황 */}
          {dCd === '104' && isInDropZone && <VisitorList openModal={openModal} setOpenModal={handleStateChange} showImg dCd={dCd} />}
          {/* 아날로그 시계 ( 가로 1, 세로 2 ) */}
          {dCd === '105' && isInDropZone && <AnalogClock viewType='d' />}
          {/* 작은 디지털 시계 ( 가로 1, 세로 1 ) */}
          {dCd === '106' && isInDropZone && <DigitalClock viewType='d' />}
          {/* 큰 디지털 시계 ( 가로 2, 세로 2 ) */}
          {dCd === '107' && isInDropZone && <ClockCalendar viewType='d' />}
          {/* 일정 관리 (캘린더 소형) */}
          {dCd === '132' && isInDropZone && <WeekCalendar userInfo={userInfo} />}
          {/* O2 산소 실시간 위젯 ( 1x1 ) */}
          {dCd === '133' && isInDropZone && <SensorMonitorItem autoRefresh={false} sensorCd='01' sensorName='O2 (산소)' dCd={dCd} />}
          {/* NO2 이산화질소 실시간 위젯 ( 1x1 ) */}
          {dCd === '134' && isInDropZone && <SensorMonitorItem autoRefresh={false} sensorCd='02' sensorName='NO2 (이산화질소)' dCd={dCd} />}
          {/* CO 일산화탄소 실시간 위젯 ( 1x1 ) */}
          {dCd === '135' && isInDropZone && <SensorMonitorItem autoRefresh={false} sensorCd='03' sensorName='CO (일산화탄소)' dCd={dCd} />}
          {/* CO2 이산화탄소 실시간 위젯 ( 1x1 ) */}
          {dCd === '136' && isInDropZone && <SensorMonitorItem autoRefresh={false} sensorCd='04' sensorName='CO2 (이산화탄소)' dCd={dCd} />}
          {/* H2S 황화화수소 실시간 위젯 ( 1x1 ) */}
          {dCd === '137' && isInDropZone && <SensorMonitorItem autoRefresh={false} sensorCd='05' sensorName='H2S (황화화수소)' dCd={dCd} />}
          {/* CH4 메탄 실시간 위젯 ( 1x1 ) */}
          {dCd === '138' && isInDropZone && <SensorMonitorItem autoRefresh={false} sensorCd='06' sensorName='CH4 (메탄)' dCd={dCd} />}
          {/* CH2O 폼알데하이드드 실시간 위젯 ( 1x1 ) */}
          {dCd === '139' && isInDropZone && <SensorMonitorItem autoRefresh={false} sensorCd='07' sensorName='CH2O (폼알데하이드)' dCd={dCd} />}
          {/* O3 오존 실시간 위젯 ( 1x1 ) */}
          {dCd === '140' && isInDropZone && <SensorMonitorItem autoRefresh={false} sensorCd='08' sensorName='O3 (오존)' dCd={dCd} />}
          {/* PM2.5 초미세먼지 실시간 위젯 ( 1x1 ) */}
          {dCd === '141' && isInDropZone && <SensorMonitorItem autoRefresh={false} sensorCd='09' sensorName='PM2.5 (초미세먼지)' dCd={dCd} />}
          {/* PM10 미세먼지 실시간 위젯 ( 1x1 ) */}
          {dCd === '142' && isInDropZone && <SensorMonitorItem autoRefresh={false} sensorCd='10' sensorName='PM10 (미세먼지)' dCd={dCd} />}
          {/* TEMP 온도 실시간 위젯 ( 1x1 ) */}
          {dCd === '143' && isInDropZone && <SensorMonitorItem autoRefresh={false} sensorCd='11' sensorName='TEMP (온도)' dCd={dCd} />}
          {/* HUMI 습도 실시간 위젯 ( 1x1 ) */}
          {dCd === '144' && isInDropZone && <SensorMonitorItem autoRefresh={false} sensorCd='12' sensorName='HUMI (습도)' dCd={dCd} />}
          {/* VOC 유기 화합물 실시간 위젯 ( 1x1 ) */}
          {dCd === '145' && isInDropZone && <SensorMonitorItem autoRefresh={false} sensorCd='13' sensorName='VOC (유기화합물)' dCd={dCd} />}
          {/* PM1 극초미세먼지 실시간 위젯 ( 1x1 ) */}
          {dCd === '146' && isInDropZone && <SensorMonitorItem autoRefresh={false} sensorCd='14' sensorName='PM1 (극초미세먼지)' dCd={dCd} />}
          {/* 체감온도 실시간 위젯 ( 1x1 ) */}
          {dCd === '157' && isInDropZone && <SensorMonitorItem autoRefresh={false} sensorCd='15' sensorName='체감온도' dCd={dCd} />}
          {/* 위치별 센서 종합현황 */}
          {dCd === '152' && isInDropZone && <SensorStatusWidget name={name} />}
        </>
      }
    </ControlStyle>
  );
};

const Cctv = ({ openModal, setOpenModal }: any) => {
  const userInfo = useRecoilValue(userState);
  interface ICameraObject {
    wsNum: string;
    nName: string;
    cName: string;
  }
  const mobile = isMobile();
  const [foldMenu] = useRecoilState<boolean>(asideFoldState);
  const { t } = useTranslation();
  // const [openModal, setOpenModal] = useState<IModal>({ status: false, type: '', title: '' });
  const [cctvDivisions, setCctvDivisions] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [cameraInfo, setCameraInfo] = useState<ICameraObject[]>([]);
  const [currentCameraInfo, setCurrentCameraInfo] = useState<ICameraObject[]>([]);
  const [nvrCdList, setNvrCdList] = useState([]);
  const [selectedCam, setSelectedCam] = useState<any>({ subCd: 0, cdName: '' });
  const [webSdkPlay, setWebSdkPlay] = useState(false);
  const [playBtn, setPlayBtn] = useState(true);

  const cctvElement = useRef<HTMLDivElement>(null);

  const onScroll = (e: any) => {
    if (userInfo.cctvProtocol === '1' && e.target.className?.includes('contents dashboard')) {
      const top = cctvElement?.current?.getBoundingClientRect();
      if (cctv.getPluginOBJECT()?.oPlugin) {
        cctv.destroy();
        setWebSdkPlay(false);
      }
      if (top && top.top > 0) {
        setPlayBtn(true);
      } else {
        setPlayBtn(false);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', (e) => onScroll(e), true);
    return () => {
      window.removeEventListener('scroll', (e) => onScroll(e), true);
    };
  }, []);

  useEffect(() => {
    if (cctv.getPluginOBJECT()?.oPlugin) {
      cctv.destroy();
      setWebSdkPlay(false);
    }
    setPlayBtn(true);
  }, [foldMenu]);

  const siteCamQuery: any = useQuery(['siteCameraGet', userInfo.hCd, userInfo.sCd], () => apiGet({ path: '/cam/site', req: { hCd: userInfo.hCd, sCd: userInfo.sCd } }), {
    retry: 3,
    enabled: !!userInfo.hCd && !!userInfo.sCd,
  });

  useEffect(() => {
    if (siteCamQuery.isSuccess && siteCamQuery.data && siteCamQuery.data.data.statusCode === 200) {
      const { cameraList } = siteCamQuery.data.data.data;
      if (cameraList.length > 0) {
        setCameraInfo(cameraList);
        const newArray = cameraList.map((el: any, index: number) => {
          return { ...el, name: `${el.nName} ${el.cName}`, cdName: `${el.nName} ${el.cName}`, subCd: index, type: 'subCd' };
        });
        setSelectedCam(newArray[0]);
        setNvrCdList(newArray);
      }
    }
  }, [siteCamQuery.isSuccess, siteCamQuery.isFetching]);

  useEffect(() => {
    const startIndex = (currentPage - 1) * cctvDivisions;
    const endIndex = startIndex + cctvDivisions;
    const currentPosts = cameraInfo.slice(startIndex, endIndex);
    setCurrentCameraInfo(currentPosts);
  }, [currentPage, cameraInfo]);

  useEffect(() => {
    setCurrentPage(1);
  }, [cctvDivisions]);

  useEffect(() => {
    setCurrentPage(selectedCam.subCd + 1);
  }, [selectedCam]);

  const onClickRefresh = () => {
    if (cctv.getPluginOBJECT()?.oPlugin) {
      cctv.destroy();
    }
    siteCamQuery.refetch();
  };

  const onClickPlay = () => {
    setWebSdkPlay(true);
    setPlayBtn(false);
  };

  if (!siteCamQuery.isSuccess || siteCamQuery.data.status !== 200 || siteCamQuery.isRefetching) {
    return (
      <div className='centered-content'>
        <PulseLoader color='rgb(0, 122, 255)' size='10px' />
      </div>
    );
  }

  return (
    <>
      <div className='cctvNarrow'>
        <div className='widget-header'>
          <div className='widget-title flex-between'>
            <span>{t('현장 CCTV')}</span>
            {/* <span>{t(name)}</span> */}
            <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
              <span className='material-symbols-rounded'>refresh</span>
            </div>
          </div>
        </div>
        {userInfo.cctvProtocol === '0' ? (
          <div className='emptyData'>
            <img src={monitoring} alt='' />
            <p className='main_text text-center'>{t('설정관리-일반관리 메뉴에서 CCTV 연결방식을 설정하세요')}</p>
          </div>
        ) : userInfo.cctvProtocol === '1' && mobile ? (
          <div className='emptyData'>
            <img src={notSupport} alt='' />
            <p className='main_text text-center'>{t('모바일 환경에서 지원되지 않는 화면입니다')}</p>
          </div>
        ) : currentPage && currentCameraInfo?.length > 0 && cameraInfo?.length > 0 ? (
          <div className='widget-body cctv-narrow' ref={cctvElement}>
            {userInfo.cctvProtocol === '2' ? (
              <>
                <SelectBox options={nvrCdList} defaultOption={selectedCam.cdName} state={selectedCam} setState={setSelectedCam} stateKey='subCd' optionHeight='height-sm' />
                <MultiVideoPlayer
                  cctvDivisions={1}
                  setCctvDivisions={setCctvDivisions}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  currentCameraInfo={currentCameraInfo}
                  totalCameraInfo={cameraInfo}
                  dashboard
                />
              </>
            ) : webSdkPlay ? (
              <>
                <SelectBox options={nvrCdList} defaultOption={selectedCam.cdName} state={selectedCam} setState={setSelectedCam} stateKey='subCd' isWebSdk hideWebSdk optionHeight='height-sm' />
                <WebSdk divisionCount={1} currentPageNumber={currentPage} setCurrentPageNumber={setCurrentPage} height='17.5rem' dashboard />
              </>
            ) : (
              <>
                <SelectBox options={nvrCdList} defaultOption={selectedCam.cdName} state={selectedCam} setState={setSelectedCam} stateKey='subCd' isWebSdk optionHeight='height-sm' />
                {playBtn && (
                  <div className='play-btn cctv' onClick={onClickPlay} role='presentation'>
                    <span className='material-symbols-rounded'>play_arrow</span>
                    {/* <span className='material-symbols-rounded'>play_circle</span> */}
                  </div>
                )}
              </>
            )}
          </div>
        ) : null}
      </div>
      <Portal openModal={openModal?.status}>{openModal && <WebSdkModal openModal={openModal} setOpenModal={setOpenModal} />}</Portal>
    </>
  );
};

const CctvWide = ({ openModal, setOpenModal }: any) => {
  const userInfo = useRecoilValue(userState);

  interface ICameraObject {
    wsNum: string;
    nName: string;
    cName: string;
  }
  const [foldMenu] = useRecoilState<boolean>(asideFoldState);
  const { t } = useTranslation();
  const [cctvDivisions, setCctvDivisions] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [cameraInfo, setCameraInfo] = useState<ICameraObject[]>([]);
  const [currentCameraInfo, setCurrentCameraInfo] = useState<ICameraObject[]>([]);
  // const [openModal, setOpenModal] = useState<IModal>({ status: false, type: '', title: '' });
  const [webSdkPlay, setWebSdkPlay] = useState(false);
  const [playBtn, setPlayBtn] = useState(true);
  const mobile = isMobile();

  const cameraRef = useRef<HTMLButtonElement>(null);
  const cctvElement = useRef<HTMLDivElement>(null);

  const onScroll = (e: any) => {
    if (userInfo.cctvProtocol === '1' && e.target.className?.includes('contents dashboard')) {
      const top = cctvElement?.current?.getBoundingClientRect();
      if (cctv.getPluginOBJECT()?.oPlugin) {
        cctv.destroy();
        setWebSdkPlay(false);
      }
      if (top && top.top > 0) {
        setPlayBtn(true);
      } else {
        setPlayBtn(false);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', (e) => onScroll(e), true);
    return () => {
      window.removeEventListener('scroll', (e) => onScroll(e), true);
    };
  }, []);

  useEffect(() => {
    if (cctv.getPluginOBJECT()?.oPlugin) {
      cctv.destroy();
      setWebSdkPlay(false);
    }
    setPlayBtn(true);
  }, [foldMenu]);

  const siteCamQuery: any = useQuery(['siteCameraGet', userInfo.hCd, userInfo.sCd], () => apiGet({ path: '/cam/site', req: { hCd: userInfo.hCd, sCd: userInfo.sCd } }), {
    retry: 3,
    enabled: !!userInfo.hCd && !!userInfo.sCd,
  });

  useEffect(() => {
    if (siteCamQuery.isSuccess && siteCamQuery.data && siteCamQuery.data.data.statusCode === 200) {
      const { cameraList } = siteCamQuery.data.data.data;
      if (cameraList.length > 0) {
        setCameraInfo(cameraList);
      }
    }
  }, [siteCamQuery.isSuccess, siteCamQuery.isFetching]);

  useEffect(() => {
    const startIndex = (currentPage - 1) * cctvDivisions;
    const endIndex = startIndex + cctvDivisions;
    const currentPosts = cameraInfo.slice(startIndex, endIndex);
    setCurrentCameraInfo(currentPosts);
  }, [currentPage, cameraInfo]);

  useEffect(() => {
    cameraRef.current?.focus();
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [cctvDivisions]);

  const onClickRefresh = () => {
    if (cctv.getPluginOBJECT()?.oPlugin) {
      cctv.destroy();
    }
    siteCamQuery.refetch();
  };

  const onClickPlay = () => {
    setWebSdkPlay(true);
    setPlayBtn(false);
  };

  const onClickNvr = (index: number) => {
    startTransition(() => {
      setCurrentPage(index + 1);
    });
  };

  if (!siteCamQuery.isSuccess || siteCamQuery.data.status !== 200 || siteCamQuery.isRefetching) {
    return (
      <div className='centered-content'>
        <PulseLoader color='rgb(0, 122, 255)' size='10px' />
      </div>
    );
  }

  return (
    <>
      <div className='cctvWide'>
        <div className='widget-header'>
          <div className='widget-title flex-between'>
            <span>{t('현장 CCTV')}</span>
            <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
              <span className='material-symbols-rounded'>refresh</span>
            </div>
          </div>
        </div>
        {userInfo.cctvProtocol === '0' ? (
          <div className='emptyData'>
            <img src={monitoring} alt='' />
            <p className='main_text text-center'>설정관리-일반관리 메뉴에서 CCTV 연결방식을 설정하세요.</p>
          </div>
        ) : userInfo.cctvProtocol === '1' && mobile ? (
          <div className='emptyData'>
            <img src={notSupport} alt='' />
            <p className='main_text text-center'>{MOBILE_NOT_SUPPORT}</p>
          </div>
        ) : (
          <div className='widget-body cctv-wide' ref={cctvElement}>
            <div className='camListWrapper'>
              {cameraInfo.map(({ wsNum, nName, cName }, index) => (
                <BtnDeep className={index + 1 === currentPage ? 'nvrBtn' : 'nvrBtn active'} ref={index + 1 === currentPage ? cameraRef : null} key={wsNum} onClick={() => onClickNvr(index)}>
                  {`${nName} ${cName}`}
                </BtnDeep>
              ))}
            </div>
            {currentPage && currentCameraInfo?.length > 0 && cameraInfo?.length > 0 ? (
              userInfo.cctvProtocol === '2' ? (
                <MultiVideoPlayer
                  cctvDivisions={1}
                  setCctvDivisions={setCctvDivisions}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  currentCameraInfo={currentCameraInfo}
                  totalCameraInfo={cameraInfo}
                  dashboard
                />
              ) : webSdkPlay ? (
                <WebSdk divisionCount={1} currentPageNumber={currentPage} setCurrentPageNumber={setCurrentPage} dashboard height='18.5rem' />
              ) : (
                playBtn && (
                  <div className='play-btn' onClick={onClickPlay} role='presentation'>
                    <span className='material-symbols-rounded'>play_circle</span>
                  </div>
                )
              )
            ) : null}
          </div>
        )}
      </div>
      <Portal openModal={openModal?.status}>{openModal && <WebSdkModal openModal={openModal} setOpenModal={setOpenModal} />}</Portal>
    </>
  );
};
